import { useEffect, useState } from 'react';
import { WifiOff, CloudOff, RefreshCw, CheckCircle2 } from 'lucide-react';
import { useOfflineReady } from '@/hooks/useOfflineReady';

export function OfflineBanner() {
  const { isOnline, syncStatus, pendingCount } = useOfflineReady();
  const [showSynced, setShowSynced] = useState(false);

  // Briefly show "All changes synced" after a successful flush
  useEffect(() => {
    if (syncStatus === 'done' && pendingCount === 0) {
      setShowSynced(true);
      const timer = setTimeout(() => setShowSynced(false), 3000);
      return () => clearTimeout(timer);
    }
    setShowSynced(false);
  }, [syncStatus, pendingCount]);

  // Offline banner
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-600 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
        <WifiOff className="h-4 w-4" />
        {pendingCount > 0
          ? `You're offline — ${pendingCount} change${pendingCount !== 1 ? 's' : ''} saved locally`
          : "You're offline — changes will be saved locally"}
      </div>
    );
  }

  // Syncing banner (back online, flushing queue)
  if (syncStatus === 'syncing') {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-blue-600 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Syncing offline changes...
      </div>
    );
  }

  // Sync error banner
  if (syncStatus === 'error' && pendingCount > 0) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-destructive text-destructive-foreground px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
        <CloudOff className="h-4 w-4" />
        Some changes couldn't sync — will retry automatically
      </div>
    );
  }

  // Brief success confirmation
  if (showSynced) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-green-600 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2 animate-in fade-in slide-in-from-top-1 duration-300">
        <CheckCircle2 className="h-4 w-4" />
        All changes synced
      </div>
    );
  }

  return null;
}
